/**
 * regexRouter.js
 * Sovereign Regex-Driven Bookmark Auto-Routing Engine
 * 
 * Evaluates incoming and existing bookmark URLs against customizable regular expressions
 * to automatically assign target folders and semantic tags.
 */

const STORAGE_KEY = 'memark_regex_routing_rules';

export const DEFAULT_REGEX_RULES = [
  {
    id: 'rule_dev',
    name: 'Git & Code Repositories',
    pattern: '^https?:\\/\\/(github|gitlab|bitbucket)\\.com\\/',
    folderTarget: 'DevTools',
    tags: ['#code', '#dev'],
    enabled: true,
  },
  {
    id: 'rule_papers',
    name: 'Scientific Research & ArXiv',
    pattern: '(arxiv\\.org|biorxiv\\.org|scholar\\.google|openreview\\.net)',
    folderTarget: 'Research',
    tags: ['#research', '#papers', '#ai'],
    enabled: true,
  },
  {
    id: 'rule_media',
    name: 'Video & Streaming Media',
    pattern: '(youtube\\.com|youtu\\.be|twitch\\.tv|vimeo\\.com)',
    folderTarget: 'Media',
    tags: ['#video', '#media'],
    enabled: true,
  },
  {
    id: 'rule_docs',
    name: 'Developer Documentation',
    pattern: '(\\/docs\\/|\\.dev\\/|developer\\.|docs\\.|readthedocs\\.io)',
    folderTarget: 'Documentation',
    tags: ['#docs', '#ref'],
    enabled: true,
  },
  {
    id: 'rule_finance',
    name: 'Crypto & Market Intelligence',
    pattern: '(coingecko\\.com|coinmarketcap\\.com|tradingview\\.com|bloomberg\\.com)',
    folderTarget: 'Finance',
    tags: ['#finance', '#crypto'],
    enabled: true,
  },
];

/**
 * Loads rules from localStorage, falling back to defaults.
 */
export function getRegexRules() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_REGEX_RULES));
      return DEFAULT_REGEX_RULES;
    }
    return JSON.parse(raw);
  } catch (err) {
    console.warn('Failed to load regex rules, using defaults', err);
    return DEFAULT_REGEX_RULES;
  }
}

/**
 * Persists rules to localStorage.
 */
export function saveRegexRules(rules) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rules));
  } catch (err) {
    console.error('Failed to save regex rules', err);
  }
}

/**
 * Tests a single URL against active rules.
 * Returns { matched: boolean, matchedRules: [], folderId: string|null, addedTags: string[] }
 */
export function matchUrlRules(url, rules = null, folders = []) {
  if (!url || typeof url !== 'string') {
    return { matched: false, matchedRules: [], folderId: null, addedTags: [] };
  }

  const activeRules = (rules || getRegexRules()).filter((r) => r.enabled);
  const matchedRules = [];
  const addedTagsSet = new Set();
  let targetFolderId = null;

  for (const rule of activeRules) {
    try {
      const reg = new RegExp(rule.pattern, 'i');
      if (reg.test(url)) {
        matchedRules.push(rule);
        (rule.tags || []).forEach((t) => addedTagsSet.add(t));

        // Find folder by name if folderTarget is string name
        if (rule.folderTarget && !targetFolderId && folders.length > 0) {
          const found = folders.find(
            (f) =>
              f.id === rule.folderTarget ||
              f.name.toLowerCase() === rule.folderTarget.toLowerCase()
          );
          if (found) targetFolderId = found.id;
        }
      }
    } catch (e) {
      // Invalid regex pattern, skip
    }
  }

  return {
    matched: matchedRules.length > 0,
    matchedRules,
    folderId: targetFolderId,
    addedTags: Array.from(addedTagsSet),
  };
}

/**
 * Batch executes all active regex rules on the entire bookmark collection.
 * Merges tags and updates folders where matched.
 */
export function applyRulesToBookmarks(bookmarks, rules = null, folders = []) {
  const currentRules = rules || getRegexRules();
  let modifiedCount = 0;
  const auditLog = [];

  const updatedBookmarks = bookmarks.map((b) => {
    const res = matchUrlRules(b.url, currentRules, folders);
    if (!res.matched) return b;

    let modified = false;
    const existingTags = Array.isArray(b.tags) ? [...b.tags] : [];
    const newTags = [...existingTags];

    // Merge new tags
    res.addedTags.forEach((tag) => {
      if (!newTags.includes(tag)) {
        newTags.push(tag);
        modified = true;
      }
    });

    // Update folder if matched and bookmark had no folder
    let targetFolder = b.folderId;
    if (res.folderId && (!b.folderId || b.folderId === 'inbox')) {
      targetFolder = res.folderId;
      modified = true;
    }

    if (modified) {
      modifiedCount++;
      auditLog.push({
        id: b.id,
        title: b.title,
        url: b.url,
        matchedRules: res.matchedRules.map((r) => r.name),
        addedTags: res.addedTags.filter((t) => !existingTags.includes(t)),
        newFolderId: targetFolder,
      });
      return {
        ...b,
        tags: newTags,
        folderId: targetFolder,
      };
    }

    return b;
  });

  return {
    updatedBookmarks,
    modifiedCount,
    auditLog,
  };
}
